import { css } from "@linaria/core";
import Button from "@ui/Button";
import { FlagLine, MessageLine } from "@ui/IconV2/react";
import { colors, fonts, spacings } from "@ui/Theme";
import React from "react";
import t from "../../../../../../../../i18n";
import type { Event } from "../../../../../../../../api/useEvents";
import { useComments } from "../../../../../../../../api/useComments";
import { comparatorBy } from "diagnostics-lib/comparator";
import Text from "@ui/Text";
import formatDate from "@ui/i18n/formatDate";
import { Tag } from "@ui/Tag";
import { analysisTitleShort } from "../../../../../../Analysis/analysisTitle";
import classNames from "classnames";
import { Markdown } from "@ui/Markdown";

const NoTag = "NoTag";
const comparator = comparatorBy("createdAt");

interface LastCommentCardProps {
  projectId: string;
  timezone: string;
  event: Event;
  associatedEventIds?: readonly string[];
  onClick?: () => void;
}

const LastCommentCard = ({
  projectId,
  timezone,
  event,
  associatedEventIds = [],
  onClick,
}: LastCommentCardProps) => {
  const comments = useComments(projectId);

  const lastEventComment = React.useMemo(() => {
    const eventComments = comments.sort(comparator).filter(c => {
      if (c.projectId !== projectId) return false;
      if (event.type === "DIAGNOSED") {
        return (
          c.faultEventId === event.id ||
          (c.faultEventId !== null && associatedEventIds.includes(c.faultEventId))
        );
      }
      return c.manualFaultEventId === event.id;
    });

    if (eventComments.length === 0) return null;

    return eventComments.at(-1) ?? null;
  }, [projectId, event, associatedEventIds, comments]);

  return (
    <div className={lastCommentCard}>
      <div className={title}>
        <span className={titleName}>
          <MessageLine color={colors.carbon800} />
          <p>{t("project.event.header.card.last_comment.title.text")}</p>
        </span>
        {onClick === undefined ? null : (
          <Button variant="link" disabled={onClick === undefined} onClick={onClick}>
            {t("project.event.header.card.last_comment.access.text")}
          </Button>
        )}
      </div>
      <div className={content}>
        {lastEventComment === null ? (
          <p className={empty}>{t("project.event.header.card.last_comment.empty.text")}</p>
        ) : (
          <div>
            <div className={author}>
              <Text type="body1SemiBold" className={authorText}>
                {lastEventComment.author.name}
              </Text>
              <Text type="body1" className={authorText}>
                {t("project.fault_event.header.last_event_comment.publishedAt.access_comment", {
                  relativeDate: formatDate(lastEventComment.publishedAt, "relative", null),
                })}
              </Text>
              {lastEventComment.analysisId && lastEventComment.metadata ? (
                <Tag size="s" className={authorTag}>
                  <FlagLine size="xs" />
                  {analysisTitleShort(
                    {
                      date: lastEventComment.metadata.date,
                      group: lastEventComment.metadata.group,
                      label: lastEventComment.metadata.label,
                    },
                    timezone,
                  )}
                </Tag>
              ) : null}
            </div>

            <Text
              type="body1"
              className={classNames({ [NoTag]: lastEventComment.analysisId === null })}
            >
              <Markdown theme={markdownClamp} defaultValue={lastEventComment.content} />
            </Text>
          </div>
        )}
      </div>
    </div>
  );
};

export default LastCommentCard;

//#region CSS
const lastCommentCard = css``;

const empty = css`
  color: ${colors.carbon500};
`;

const title = css`
  display: flex;
  align-items: center;
  gap: ${spacings.xs};
`;
const titleName = css`
  display: inline-flex;
  align-items: center;
  gap: ${spacings.xxs};
  color: ${colors.carbon800};
  p {
    font: ${fonts.body1SemiBold};
  }
`;
const content = css`
  display: flex;
  flex-direction: column;
  gap: ${spacings.xs};
  color: ${colors.carbon800};
  margin-top: ${spacings.m};

  strong {
    color: ${colors.carbon900};
  }
`;

const author = css`
  display: flex;
  align-items: center;
  gap: ${spacings.xs};
  min-width: 0;
  white-space: nowrap;
`;

const authorText = css`
  color: ${colors.carbon700};
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const authorTag = css`
  flex-shrink: 0;
`;

const markdownClamp = css`
  overflow: hidden;

  @supports (-webkit-line-clamp: 3) {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: initial;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
  }
`;
//#endregion CSS
